import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Loader2 } from 'lucide-react'
import { useLanguage } from '@/context/LanguageContext'
import {
  GET_CONTENT_BLOCK_USAGES,
  GET_PRODUCT_CONTAINERS_BY_IDS,
} from '../contentBlockQueries'
import type { ContentBlockType } from '../types'

interface ContentBlockUsagesProps {
  id: number
}

type UsageRow = { id: number | string; label: string; href: string }

const MAX_VISIBLE_PER_GROUP = 10

// Same "current language first, then first non-empty, then a fallback"
// convention as `getCategoryName`/`getPageTitle` in the forms — `||` (not
// `??`) so a profile whose name/title is an empty string (not just missing)
// is skipped too, instead of rendering a blank, unclickable-looking link.
function pickName(
  profiles: { name?: string | null; language: string }[] | null | undefined,
  currentLang: string
): string | undefined {
  return (
    profiles?.find(p => p.language === currentLang && p.name?.trim())?.name ||
    profiles?.find(p => p.name?.trim())?.name ||
    undefined
  )
}

function pickPageTitle(
  page: {
    id: string
    code: string
    page_profiles: { title?: string | null; language: string }[]
  },
  currentLang: string
): string {
  return (
    page.page_profiles?.find(p => p.language === currentLang && p.title?.trim())
      ?.title ||
    page.page_profiles?.find(p => p.title?.trim())?.title ||
    page.code ||
    `#${page.id}`
  )
}

function UsageGroup({
  label,
  rows,
}: {
  label: string
  rows: UsageRow[]
}) {
  const [expanded, setExpanded] = useState(false)
  if (rows.length === 0) return null
  const visibleRows = expanded ? rows : rows.slice(0, MAX_VISIBLE_PER_GROUP)
  const hiddenCount = rows.length - visibleRows.length

  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">
        {label} ({rows.length})
      </p>
      <ul className="space-y-0.5">
        {visibleRows.map(row => (
          <li key={row.id}>
            <Link
              to={row.href}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-primary hover:underline break-words"
            >
              {row.label}
            </Link>
          </li>
        ))}
      </ul>
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-xs text-muted-foreground hover:underline"
        >
          Show {hiddenCount} more
        </button>
      )}
    </div>
  )
}

/**
 * "Used on" list shown directly below the Update button on both content
 * block edit forms (drawer `ContentBlockForm` + modal `ContentBlockFormBody`).
 * Edit mode only — a block that doesn't exist yet can't be used anywhere.
 */
export default function ContentBlockUsages({ id }: ContentBlockUsagesProps) {
  const { allLanguages, languageId } = useLanguage()
  const currentLang = allLanguages[languageId] || allLanguages[0]

  const { data, loading, error } = useQuery(GET_CONTENT_BLOCK_USAGES, {
    variables: { id },
    fetchPolicy: 'no-cache',
  })

  const block = data?.findUniqueContentBlock

  return (
    <div className="rounded-md border p-3 space-y-3">
      <p className="text-sm font-medium">Used on</p>

      {loading && !block && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading usage…
        </p>
      )}

      {error && (
        <p className="text-sm text-destructive">
          Couldn't load where this block is used.
        </p>
      )}

      {block && <ContentBlockUsagesList block={block} currentLang={currentLang} />}
    </div>
  )
}

function ContentBlockUsagesList({
  block,
  currentLang,
}: {
  block: {
    type: ContentBlockType
    page?: {
      id: string
      code: string
      page_profiles: { title?: string | null; language: string }[]
    } | null
    categories: {
      id: number
      category_profiles: { name?: string | null; language: string }[]
    }[]
    collections: {
      id: number
      collection_profiles: { name?: string | null; language: string }[]
    }[]
    product_containers: {
      id: number
      reference?: string | null
      advanced_product?: {
        advanced_product_profiles: { name?: string | null; language: string }[]
      } | null
      single_product?: {
        product_profiles: { name?: string | null; language: string }[]
      } | null
    }[]
    // Manual `product_grid` picks, ordered — ids only (see
    // `GET_PRODUCT_CONTAINERS_BY_IDS`'s doc comment for why names are
    // resolved via a second query instead of nested fields here).
    grid_product_containers: { id: number }[]
    shopsettings_home_page_content_blocks: { id: number }[]
  }
  currentLang: string
}) {
  const isProductGrid = block.type === 'product_grid'
  const gridProductIds = block.grid_product_containers.map(p => p.id)

  const { data: gridProductsData } = useQuery(GET_PRODUCT_CONTAINERS_BY_IDS, {
    variables: { ids: gridProductIds },
    skip: !isProductGrid || gridProductIds.length === 0,
    fetchPolicy: 'no-cache',
  })
  const pageRows: UsageRow[] = block.page
    ? [
        {
          id: block.page.id,
          label: pickPageTitle(block.page, currentLang),
          href: `/pages/${block.page.id}`,
        },
      ]
    : []

  const categoryRows: UsageRow[] = block.categories.map(cat => {
    const name = pickName(cat.category_profiles, currentLang)
    return {
      id: cat.id,
      label: name ? `${name} (#${cat.id})` : `#${cat.id}`,
      href: '/products/categories',
    }
  })

  const collectionRows: UsageRow[] = block.collections.map(col => {
    const name = pickName(col.collection_profiles, currentLang)
    return {
      id: col.id,
      label: name ? `${name} (#${col.id})` : `#${col.id}`,
      href: '/products/collections',
    }
  })

  const productRows: UsageRow[] = block.product_containers.map(product => {
    const name =
      pickName(product.advanced_product?.advanced_product_profiles, currentLang) ||
      pickName(product.single_product?.product_profiles, currentLang) ||
      product.reference ||
      `#${product.id}`
    return {
      id: product.id,
      label: name,
      href: `/products/${product.id}`,
    }
  })

  // Names resolved via the separate `GET_PRODUCT_CONTAINERS_BY_IDS` query
  // (see its doc comment) — an id with no match yet (still loading, or a
  // deleted product) falls back to `#id` rather than being dropped, since
  // the backend would reject a stale id at save time anyway.
  const gridProductContainers: {
    id: number
    reference?: string | null
    advanced_product?: {
      advanced_product_profiles: { name?: string | null; language: string }[]
    } | null
    single_product?: {
      product_profiles: { name?: string | null; language: string }[]
    } | null
  }[] = gridProductsData?.findManyProductContainer ?? []
  const gridProductById = new Map(
    gridProductContainers.map(p => [p.id, p])
  )
  const gridProductRows: UsageRow[] = gridProductIds.map(id => {
    const product = gridProductById.get(id)
    const name = product
      ? pickName(product.advanced_product?.advanced_product_profiles, currentLang) ||
        pickName(product.single_product?.product_profiles, currentLang) ||
        product.reference ||
        `#${id}`
      : `#${id}`
    return { id, label: name, href: `/products/${id}` }
  })

  const homepageRows: UsageRow[] =
    block.shopsettings_home_page_content_blocks.length > 0
      ? [{ id: 'homepage', label: 'Homepage', href: '/settings/home-page' }]
      : []

  const isEmpty =
    pageRows.length === 0 &&
    categoryRows.length === 0 &&
    collectionRows.length === 0 &&
    productRows.length === 0 &&
    gridProductRows.length === 0 &&
    homepageRows.length === 0

  if (isEmpty) {
    return (
      <p className="text-sm text-muted-foreground">Not used anywhere yet</p>
    )
  }

  return (
    <div className="space-y-3">
      <UsageGroup label="Page" rows={pageRows} />
      <UsageGroup label="Categories" rows={categoryRows} />
      <UsageGroup label="Collections" rows={collectionRows} />
      <UsageGroup label="Shown on product pages" rows={productRows} />
      {isProductGrid && (
        <UsageGroup label="Products in this grid" rows={gridProductRows} />
      )}
      <UsageGroup label="Homepage" rows={homepageRows} />
    </div>
  )
}
